import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import ReactPlayer from "react-player";
import { api, formatApiError } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionContext";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import { useDoxaConfirm } from "@/components/doxa/DoxaConfirmDialog";
import {
  Archive,
  ArrowLeft,
  Ban,
  CalendarDays,
  Film,
  GraduationCap,
  MapPin,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  Play,
  QrCode,
  RefreshCw,
  RotateCcw,
  Square,
  Users,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { tr, LESSON_STATUS_LABEL, LESSON_TYPE_LABEL, ATTENDANCE_STATUS_LABEL, CHECKIN_METHOD_LABEL, brDateTime, fmtLessonRange } from "@/lib/labels";
import { LessonFormModal } from "@/pages/admin/Lessons";
import LessonAnalyticsSummary from "@/components/analytics/LessonAnalyticsSummary";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function lessonStatusClass(status) {
  if (status === "OPEN") return "doxa-badge-success";
  if (status === "CANCELLED") return "doxa-badge-critical";
  if (status === "ARCHIVED") return "doxa-badge-neutral";
  return "doxa-badge-neutral";
}

function SectionHeader({ icon: Icon, title, description, action }) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold text-[#1F1F1F]">{title}</h2>
          {description && <p className="mt-0.5 text-xs leading-relaxed text-[#6B655E]">{description}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function LessonActionsMenu({ lesson, onEdit, onCancel, onArchive, onReactivate }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-[#E0D9CD] bg-white text-[#8A8378] transition hover:border-[#F75A1B]/35 hover:bg-[#FFF0E9] hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 data-[state=open]:border-[#F75A1B]/35 data-[state=open]:bg-[#FFF0E9] data-[state=open]:text-[#F75A1B]"
          aria-label={`Ações da aula ${lesson.title}`}
          data-testid="lesson-actions-menu"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={7} className="w-60 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações da aula</span>
          <span className="mt-0.5 block truncate text-xs font-semibold normal-case tracking-normal text-[#4A4640]">{lesson.title}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        <DropdownMenuItem onSelect={onEdit} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid="edit-lesson-btn">
          <Pencil className="h-4 w-4" /> Editar aula
        </DropdownMenuItem>
        {lesson.status !== "CANCELLED" && lesson.status !== "ARCHIVED" && (
          <>
            <DropdownMenuSeparator className="bg-[#EBE5DB]" />
            <DropdownMenuItem onSelect={onCancel} className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#C53030] focus:bg-[#FFF1F1] focus:text-[#C53030]" data-testid="cancel-lesson-btn">
              <Ban className="h-4 w-4" /> Cancelar aula
            </DropdownMenuItem>
          </>
        )}
        {lesson.status !== "ARCHIVED" && (
          <DropdownMenuItem onSelect={onArchive} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#F6F3EE] focus:text-[#4A4640]" data-testid="archive-lesson-btn">
            <Archive className="h-4 w-4" /> Arquivar aula
          </DropdownMenuItem>
        )}
        {(lesson.status === "ARCHIVED" || lesson.status === "CANCELLED") && (
          <DropdownMenuItem onSelect={onReactivate} className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#2F855A] focus:bg-[#EDF8F1] focus:text-[#2F855A]" data-testid="reactivate-lesson-btn">
            <RotateCcw className="h-4 w-4" /> Reativar como agendada
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function LessonDetail() {
  const confirmAction = useDoxaConfirm();
  const { id } = useParams();
  const { can } = usePermissions();
  const canManage = can("lesson.manage");
  const canOperate = can("lesson.operate");
  const canCorrect = can("attendance.correct");
  const canJustify = can("attendance.justify");
  const canOnline = can("attendance.online_release");
  const [lesson, setLesson] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [perms, setPerms] = useState([]);
  const [justify, setJustify] = useState(null);
  const [justifyText, setJustifyText] = useState("");
  const [classGroups, setClassGroups] = useState([]);
  const [editing, setEditing] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [bulkOnline, setBulkOnline] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [videoProgress, setVideoProgress] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const loadStudents = async ({ silent = false } = {}) => {
    try {
      const { data } = await api.get("/students", { params: { include_inactive: showInactive } });
      setStudents(data);
      return true;
    } catch (error) {
      if (!silent) toast.error(formatApiError(error));
      return false;
    }
  };

  const load = async ({ silent = false, includeProgress = true } = {}) => {
    try {
      const [{ data }, attendanceResponse, permsResponse] = await Promise.all([
        api.get(`/lessons/${id}`),
        api.get(`/lessons/${id}/attendance`),
        api.get(`/lessons/${id}/online-perms`),
      ]);
      setLesson(data);
      setAttendance(attendanceResponse.data);
      setPerms(permsResponse.data);

      if (includeProgress) {
        if (data.video_url && (data.lesson_type === "ONLINE" || data.lesson_type === "HYBRID")) {
          try {
            const progressResponse = await api.get(`/lessons/${id}/progress`);
            setVideoProgress(progressResponse.data);
          } catch {
            setVideoProgress([]);
          }
        } else {
          setVideoProgress([]);
        }
      }

      setLastSyncedAt(Date.now());
      return true;
    } catch (error) {
      if (!silent) toast.error(formatApiError(error));
      return false;
    }
  };

  useEffect(() => {
    void load();
    api.get("/class-groups", { params: { include_inactive: true } })
      .then((response) => setClassGroups(response.data))
      .catch((error) => toast.error(formatApiError(error)));
  }, [id]);
  useEffect(() => { void loadStudents(); }, [showInactive]);

  useVisibleRefresh(
    async (reason) => {
      if (reason === "interval") {
        await load({ silent: true, includeProgress: false });
        return;
      }
      await Promise.all([
        load({ silent: true }),
        loadStudents({ silent: true }),
      ]);
    },
    { intervalMs: lesson?.status === "OPEN" ? 5000 : 0 },
  );

  const open = async () => {
    try { await api.post(`/lessons/${id}/open`); toast.success("Aula aberta!"); await load({ silent: true }); } catch (error) { toast.error(formatApiError(error)); }
  };
  const close = () => confirmAction({
    title: "Encerrar aula?",
    description: <>A aula <strong className="font-semibold text-[#1F1F1F]">{lesson?.title || "selecionada"}</strong> será marcada como encerrada.</>,
    confirmLabel: "Encerrar aula",
    loadingLabel: "Encerrando…",
    variant: "warning",
    dataTestId: "confirm-close-lesson",
    onConfirm: async () => {
      await api.post(`/lessons/${id}/close`);
      toast.success("Aula encerrada");
      await load({ silent: true });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const cancelLesson = () => confirmAction({
    title: "Cancelar aula?",
    description: <>A aula <strong className="font-semibold text-[#1F1F1F]">{lesson?.title || "selecionada"}</strong> será cancelada e não será possível registrar mais presenças.</>,
    confirmLabel: "Cancelar aula",
    loadingLabel: "Cancelando…",
    variant: "destructive",
    dataTestId: "confirm-cancel-lesson",
    onConfirm: async () => {
      await api.post(`/lessons/${id}/cancel`);
      toast.success("Aula cancelada");
      await load({ silent: true });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const archive = () => confirmAction({
    title: "Arquivar aula?",
    description: <>A aula <strong className="font-semibold text-[#1F1F1F]">{lesson?.title || "selecionada"}</strong> deixará de aparecer nas listagens ativas.</>,
    confirmLabel: "Arquivar",
    loadingLabel: "Arquivando…",
    variant: "warning",
    dataTestId: "confirm-archive-lesson",
    onConfirm: async () => {
      await api.post(`/lessons/${id}/archive`);
      toast.success("Aula arquivada");
      await load({ silent: true });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const reactivate = () => confirmAction({
    title: "Reativar aula?",
    description: <>A aula <strong className="font-semibold text-[#1F1F1F]">{lesson?.title || "selecionada"}</strong> voltará como <strong className="font-semibold text-[#1F1F1F]">Agendada</strong>.</>,
    confirmLabel: "Reativar aula",
    loadingLabel: "Reativando…",
    variant: "positive",
    dataTestId: "confirm-reactivate-lesson",
    onConfirm: async () => {
      await api.post(`/lessons/${id}/reactivate`);
      toast.success("Aula reativada");
      await load({ silent: true });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const submitJustify = async () => {
    if (!justifyText.trim()) { toast.error("Informe a justificativa."); return; }
    try {
      await api.post("/attendance/justify", { student_id: justify.id, lesson_id: id, justification_reason: justifyText });
      toast.success("Ausência justificada");
      setJustify(null);
      setJustifyText("");
      await load({ silent: true });
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const togglePerm = async (student) => {
    const allowed = perms.find((permission) => permission.student_id === student.id && permission.online_checkin_allowed);
    try {
      if (allowed) {
        await api.delete("/online-perms", { params: { student_id: student.id, lesson_id: id } });
        toast.success("Liberação removida");
      } else {
        await api.post("/online-perms", { student_id: student.id, lesson_id: id, reason: "" });
        toast.success("Aluno liberado para check-in online");
      }
      await load({ silent: true });
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const markManual = async (student, status) => {
    try {
      await api.post("/attendance/manual", { student_id: student.id, lesson_id: id, status, correction_reason: "Lançamento manual" });
      toast.success("Presença atualizada");
      await load({ silent: true });
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const bulkGrant = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/lessons/${id}/online-perms/bulk-grant`);
      toast.success(`Check-in online liberado para ${data.granted} aluno(s).`);
      await load({ silent: true });
      setBulkOnline(false);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBulkBusy(false);
    }
  };
  const bulkRevoke = () => confirmAction({
    title: "Encerrar check-in online da turma?",
    description: <>O check-in online da turma será encerrado para a aula <strong className="font-semibold text-[#1F1F1F]">{lesson?.title || "selecionada"}</strong>.</>,
    confirmLabel: "Encerrar check-in",
    loadingLabel: "Encerrando…",
    variant: "warning",
    dataTestId: "confirm-bulk-revoke-online-checkin",
    onConfirm: async () => {
      setBulkBusy(true);
      try {
        const { data } = await api.post(`/lessons/${id}/online-perms/bulk-revoke`);
        toast.success(`Check-in online da turma encerrado (${data.revoked}).`);
        await load({ silent: true });
      } finally {
        setBulkBusy(false);
      }
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  if (!lesson) return <div className="p-8 text-[#6B655E]">Carregando…</div>;

  const checkinUrl = lesson.qr_code_token ? `${window.location.origin}/checkin/${lesson.id}/${lesson.qr_code_token}` : null;
  const lessonStudents = students.filter((student) => student.class_group_id === lesson.class_group_id);
  const activeStudents = lessonStudents.filter((student) => student.status === "ACTIVE");
  const activeCount = activeStudents.length;
  const classGroup = classGroups.find((group) => group.id === lesson.class_group_id);
  const justifiedAttendance = attendance.filter((row) => row.status === "JUSTIFIED_ABSENCE");
  const checkinDescription = lesson.status === "OPEN"
    ? "Aula aberta. Os alunos presenciais podem escanear este QR Code agora."
    : lesson.status === "CLOSED"
      ? "Aula encerrada. O QR permanece como referência, mas novos check-ins não são aceitos."
      : "Aula ainda não aberta. Quando você abrir, este QR Code passará a registrar presenças.";

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-5 md:p-6" data-testid="lesson-detail-page">
      <Link to="/lessons" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6B655E] transition hover:text-[#F75A1B]">
        <ArrowLeft className="h-4 w-4" /> Voltar para aulas
      </Link>

      <section className="overflow-hidden rounded-[20px] border border-[#E0D9CD] bg-white">
        <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><CalendarDays className="h-4 w-4" /></span>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Operação da aula</p>
            </div>
            <h1 className="mt-3 break-words font-display text-2xl font-bold text-[#111111] md:text-3xl">{lesson.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#6B655E]">
              <span>{fmtLessonRange(lesson.lesson_date, lesson.start_time, lesson.end_time)}</span>
              <span aria-hidden="true">·</span>
              <span>{tr(LESSON_TYPE_LABEL, lesson.lesson_type)}</span>
              {classGroup?.name && <><span aria-hidden="true">·</span><span className="inline-flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5" /> {classGroup.name}</span></>}
              {lesson.location && <><span aria-hidden="true">·</span><span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {lesson.location}</span></>}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`doxa-badge ${lessonStatusClass(lesson.status)}`} data-testid="lesson-status">{tr(LESSON_STATUS_LABEL, lesson.status)}</span>
              {lastSyncedAt && (
                <span className="inline-flex items-center gap-1 text-[11px] text-[#8A8378]" data-testid="lesson-auto-refresh">
                  <RefreshCw className="h-3 w-3" /> {lesson.status === "OPEN" ? "Atualização automática · 5 s" : "Atualiza ao voltar para a tela"}
                </span>
              )}
            </div>
          </div>

          {(canOperate || canManage) && (
            <div className="flex shrink-0 items-center gap-2">
              {canOperate && lesson.status !== "OPEN" && lesson.status !== "CANCELLED" && lesson.status !== "ARCHIVED" && (
                <button onClick={open} className="doxa-btn-primary inline-flex h-10 items-center gap-2 px-4" data-testid="open-lesson-btn">
                  <Play className="h-4 w-4" /> Abrir aula
                </button>
              )}
              {canOperate && lesson.status === "OPEN" && (
                <button onClick={close} className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#1F1F1F] px-4 text-sm font-semibold text-white transition hover:bg-black" data-testid="close-lesson-btn">
                  <Square className="h-4 w-4" /> Encerrar
                </button>
              )}
              {canManage && (
                <LessonActionsMenu lesson={lesson} onEdit={() => setEditing(true)} onCancel={cancelLesson} onArchive={archive} onReactivate={reactivate} />
              )}
            </div>
          )}
        </div>
      </section>

      <LessonAnalyticsSummary lesson={lesson} students={activeStudents} attendance={attendance} videoProgress={videoProgress} />

      {checkinUrl && lesson.status !== "CANCELLED" && (
        <section className="rounded-[18px] border border-[#E0D9CD] bg-white p-5 sm:p-6">
          <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <SectionHeader icon={QrCode} title="QR Code de check-in" description={checkinDescription} />
              <div className="rounded-xl border border-[#EBE5DB] bg-[#FAF7F1] p-3">
                <p className="break-all text-xs leading-relaxed text-[#6B655E]" data-testid="checkin-url">{checkinUrl}</p>
              </div>
            </div>
            <div className="mx-auto rounded-[18px] border border-[#E0D9CD] bg-white p-4 shadow-[0_10px_30px_rgba(68,53,35,0.04)]" data-testid="qr-code-display">
              <QRCodeSVG value={checkinUrl} size={210} fgColor="#111111" />
            </div>
          </div>
        </section>
      )}

      {lesson.video_url && (lesson.lesson_type === "ONLINE" || lesson.lesson_type === "HYBRID") && (
        <section className="rounded-[18px] border border-[#E0D9CD] bg-white p-5 sm:p-6" data-testid="lesson-video-admin">
          <SectionHeader icon={Film} title="Vídeo da aula" description="Conteúdo vinculado à aula para acompanhamento no portal." />
          <div className="relative w-full max-w-4xl overflow-hidden rounded-[16px] bg-black" style={{ paddingTop: "56.25%" }}>
            <ReactPlayer src={lesson.video_url} controls style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
          </div>
        </section>
      )}

      {lesson.video_url && (lesson.lesson_type === "ONLINE" || lesson.lesson_type === "HYBRID") && (
        <section className="rounded-[18px] border border-[#E0D9CD] bg-white p-5 sm:p-6" data-testid="lesson-video-progress">
          <SectionHeader icon={Film} title="Progresso do vídeo por aluno" description="Conclusão exige 90% assistido e continua separada da presença." />
          {videoProgress.length === 0 ? (
            <p className="rounded-xl bg-[#FAF7F1] px-4 py-5 text-sm text-[#6B655E]" data-testid="video-progress-empty">Nenhum aluno ativo na turma.</p>
          ) : (
            <div className="divide-y divide-[#EBE5DB]">
              {videoProgress.map((progress) => {
                const status = progress.completed ? "Concluída" : (progress.progress_percent > 0 ? "Em andamento" : "Não iniciada");
                const cls = progress.completed ? "doxa-badge-success" : (progress.progress_percent > 0 ? "doxa-badge-warning" : "doxa-badge-neutral");
                return (
                  <div key={progress.student_id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0" data-testid={`video-progress-row-${progress.student_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-semibold text-[#1F1F1F]">{progress.full_name}</p>
                      <div className="mt-2 h-2 w-full max-w-sm overflow-hidden rounded-full bg-[#EBE5DB]">
                        <div className="h-full rounded-full bg-[#F75A1B]" style={{ width: `${Math.min(progress.progress_percent || 0, 100)}%` }} />
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className={`doxa-badge ${cls}`} data-testid={`video-progress-status-${progress.student_id}`}>{status}</span>
                      <p className="mt-1 text-xs font-medium text-[#6B655E]">{progress.progress_percent || 0}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-[18px] border border-[#E0D9CD] bg-white p-5 sm:p-6">
          <SectionHeader
            icon={Users}
            title="Alunos da turma & presença"
            description="Registre ou corrija a situação desta aula sem sair da operação."
            action={(
              <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-[#6B655E]">
                <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} className="h-4 w-4 accent-[#F75A1B]" data-testid="show-inactive-toggle" />
                Mostrar históricos/inativos
              </label>
            )}
          />
          <div className="divide-y divide-[#EBE5DB]">
            {lessonStudents.map((student) => {
              const record = attendance.find((row) => row.student_id === student.id);
              return (
                <div key={student.id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`att-row-${student.id}`}>
                  <div className="min-w-0">
                    <p className="break-words text-sm font-semibold text-[#1F1F1F]">{student.full_name}</p>
                    {record ? (
                      <p className="mt-0.5 text-xs text-[#6B655E]">{tr(ATTENDANCE_STATUS_LABEL, record.status)} · {tr(CHECKIN_METHOD_LABEL, record.checkin_method)}</p>
                    ) : (
                      <p className="mt-0.5 text-xs text-[#A59C90]">Sem registro nesta aula</p>
                    )}
                  </div>
                  {(canCorrect || canJustify) && (
                    <div className="flex shrink-0 flex-wrap gap-1.5">
                      {(() => {
                        const hasSelection = !!record;
                        const present = record?.status?.startsWith("PRESENT");
                        const justified = record?.status === "JUSTIFIED_ABSENCE";
                        const absent = record?.status === "ABSENT";
                        const dim = (active) => (hasSelection && !active ? "opacity-40" : "");
                        return (
                          <>
                            {canCorrect && <button aria-pressed={present} onClick={() => markManual(student, "PRESENT_MANUAL")} className={`rounded-lg px-2.5 py-1.5 text-xs transition-opacity ${present ? "bg-[#2F855A] font-semibold text-white" : "bg-[#E6F4EA] text-[#2F855A]"} ${dim(present)}`} data-testid={`mark-present-${student.id}`}>Presente</button>}
                            {canJustify && <button aria-pressed={justified} onClick={() => { setJustify(student); setJustifyText(""); }} className={`rounded-lg px-2.5 py-1.5 text-xs transition-opacity ${justified ? "bg-[#C05621] font-semibold text-white" : "bg-[#FEEBC8] text-[#C05621]"} ${dim(justified)}`} data-testid={`justify-${student.id}`}>Justif.</button>}
                            {canCorrect && <button aria-pressed={absent} onClick={() => markManual(student, "ABSENT")} className={`rounded-lg px-2.5 py-1.5 text-xs transition-opacity ${absent ? "bg-[#C53030] font-semibold text-white" : "bg-[#FED7D7] text-[#C53030]"} ${dim(absent)}`} data-testid={`mark-absent-${student.id}`}>Ausente</button>}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
            {lessonStudents.length === 0 && <p className="py-5 text-sm text-[#6B655E]">Nenhum aluno na turma.</p>}
          </div>
        </section>

        <section className="rounded-[18px] border border-[#E0D9CD] bg-white p-5 sm:p-6">
          <SectionHeader
            icon={Wifi}
            title="Check-in online"
            description={lesson.lesson_type === "ONLINE" ? "Aula online — todos os alunos da turma podem fazer check-in pelo portal." : "Em aulas presenciais/híbridas, somente alunos liberados podem fazer check-in online."}
          />
          {canOnline && (lesson.lesson_type === "ONLINE" || lesson.lesson_type === "HYBRID") && (
            <div className="mb-4 flex flex-wrap gap-2 rounded-xl bg-[#FAF7F1] p-3">
              <button onClick={() => setBulkOnline(true)} className="doxa-btn-primary inline-flex items-center gap-1.5 px-3 py-2 text-sm" data-testid="bulk-online-grant-btn"><Wifi className="h-4 w-4" /> Liberar turma</button>
              <button onClick={bulkRevoke} disabled={bulkBusy} className="inline-flex items-center gap-1.5 rounded-xl border border-[#E0D9CD] bg-white px-3 py-2 text-sm font-medium text-[#6B655E] disabled:opacity-50" data-testid="bulk-online-revoke-btn"><WifiOff className="h-4 w-4" /> Encerrar liberação</button>
            </div>
          )}
          <div className="divide-y divide-[#EBE5DB]">
            {lessonStudents.map((student) => {
              const allowed = perms.find((permission) => permission.student_id === student.id && permission.online_checkin_allowed);
              return (
                <div key={student.id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                  <span className="min-w-0 break-words text-sm font-medium text-[#1F1F1F]">{student.full_name}</span>
                  {canOnline ? (
                    <button onClick={() => togglePerm(student)}
                      className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium ${allowed ? "bg-[#FFEBE3] text-[#F75A1B]" : "bg-[#F3EFE7] text-[#4A4640]"}`}
                      data-testid={`toggle-online-${student.id}`}>
                      {allowed ? <><Wifi className="h-3 w-3" /> Liberado</> : <><WifiOff className="h-3 w-3" /> Não liberado</>}
                    </button>
                  ) : (
                    <span className={`doxa-badge ${allowed ? "doxa-badge-orange" : "doxa-badge-neutral"}`}>{allowed ? "Liberado" : "Não liberado"}</span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <section className="rounded-[18px] border border-[#E0D9CD] bg-white p-5 sm:p-6" data-testid="lesson-justifications">
        <SectionHeader icon={MessageSquareText} title="Justificativas desta aula" description="Registros preservados para acompanhamento da frequência." />
        <div className="space-y-3">
          {justifiedAttendance.map((record) => {
            const student = students.find((item) => item.id === record.student_id);
            return (
              <div key={record.id} className="rounded-xl border border-[#EBE5DB] bg-[#FFFEFC] p-3.5" data-testid={`lesson-justify-${record.student_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-[#1F1F1F]">{student?.full_name || "Aluno"}</p>
                  <span className="doxa-badge doxa-badge-warning">{tr(ATTENDANCE_STATUS_LABEL, record.status)}</span>
                </div>
                {record.justification_reason && <p className="mt-2 text-sm leading-relaxed text-[#4A4640]">{record.justification_reason}</p>}
                {record.justified_at && <p className="mt-1.5 text-xs text-[#8A8378]">Registrada em {brDateTime(record.justified_at)}</p>}
              </div>
            );
          })}
          {justifiedAttendance.length === 0 && (
            <p className="rounded-xl bg-[#FAF7F1] px-4 py-5 text-sm text-[#6B655E]">Nenhuma justificativa registrada para esta aula.</p>
          )}
        </div>
      </section>

      {bulkOnline && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#1F1712]/45 p-4 backdrop-blur-[2px]" onClick={() => !bulkBusy && setBulkOnline(false)}>
          <div className="w-full max-w-md overflow-hidden rounded-[20px] border border-white/70 bg-white shadow-[0_24px_80px_rgba(45,31,20,.18)]" onClick={(event) => event.stopPropagation()} data-testid="bulk-online-modal">
            <div className="flex items-start justify-between gap-3 border-b border-[#EBE5DB] px-5 py-5">
              <div>
                <h2 className="font-display text-lg font-bold">Liberar check-in online?</h2>
                <p className="mt-1 text-xs leading-relaxed text-[#6B655E]">A liberação será aplicada a <strong>{activeCount}</strong> aluno(s) ativo(s) desta turma.</p>
              </div>
              <button type="button" onClick={() => !bulkBusy && setBulkOnline(false)} className="grid h-8 w-8 place-items-center rounded-lg text-[#8A8378] hover:bg-[#F6F3EE]" aria-label="Fechar"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex gap-2 bg-[#FAF7F1] px-5 py-4">
              <button onClick={() => setBulkOnline(false)} disabled={bulkBusy} className="flex-1 rounded-xl border border-[#E0D9CD] bg-white px-4 py-2.5 text-sm font-semibold text-[#4A4640] disabled:opacity-50" data-testid="bulk-online-cancel">Cancelar</button>
              <button onClick={bulkGrant} disabled={bulkBusy} className="doxa-btn-primary flex-1 px-4 py-2.5 text-sm" data-testid="bulk-online-confirm">{bulkBusy ? "Liberando…" : "Confirmar"}</button>
            </div>
          </div>
        </div>
      )}

      {justify && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#1F1712]/45 p-4 backdrop-blur-[2px]" onClick={() => setJustify(null)}>
          <div className="w-full max-w-md overflow-hidden rounded-[20px] border border-white/70 bg-white shadow-[0_24px_80px_rgba(45,31,20,.18)]" onClick={(event) => event.stopPropagation()} data-testid="justify-modal">
            <div className="flex items-start justify-between gap-3 border-b border-[#EBE5DB] px-5 py-5">
              <div>
                <h3 className="font-display text-lg font-bold">Ausência justificada</h3>
                <p className="mt-1 text-xs text-[#6B655E]">{justify.full_name} · este texto ficará registrado.</p>
              </div>
              <button type="button" onClick={() => setJustify(null)} className="grid h-8 w-8 place-items-center rounded-lg text-[#8A8378] hover:bg-[#F6F3EE]" aria-label="Fechar"><X className="h-4 w-4" /></button>
            </div>
            <div className="px-5 py-5">
              <textarea value={justifyText} onChange={(event) => setJustifyText(event.target.value)} rows={4}
                className="w-full rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm outline-none transition placeholder:text-[#A59C90] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15"
                placeholder="Ex.: Aluno avisou previamente — viagem da família." data-testid="justify-text" />
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-[#EBE5DB] bg-[#FAF7F1] px-5 py-4 sm:flex-row sm:justify-end">
              <button onClick={() => setJustify(null)} className="h-10 rounded-xl border border-[#E0D9CD] bg-white px-4 text-sm font-semibold text-[#4A4640]">Cancelar</button>
              <button onClick={submitJustify} className="doxa-btn-primary h-10 px-4" data-testid="justify-submit">Salvar justificativa</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <LessonFormModal lesson={lesson} classGroups={classGroups} onClose={() => setEditing(false)} onSaved={load} />
      )}
    </div>
  );
}
